<template>
  <div style="height: fit-content">
    <div
      v-if="showTopBar"
      style="
        background: rgb(242, 242, 242);
        height: 36px;
        border: 1px solid rgb(191, 191, 191);
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
        display: flex;
        align-items: center;
        padding: 0 12px;
        gap: 8px;
      "
    >
      <svg fill="none" height="12" viewBox="0 0 12 12" width="12" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="6" fill="#F22929" r="6" />
      </svg>
      <svg fill="none" height="12" viewBox="0 0 12 12" width="12" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="6" fill="#E5B91A" r="6" />
      </svg>
      <svg fill="none" height="12" viewBox="0 0 12 12" width="12" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="6" fill="#00B929" r="6" />
      </svg>
    </div>
    <div
      v-if="loading"
      class="tw:border-default-500 tw:bg-white tw:border tw:border-t-0 tw:flex tw:justify-center tw:items-center tw:h-96"
    >
      <EdroneSkeletonLoader />
    </div>
    <div
      v-else-if="parsedHtml"
      :class="{ 'tw:border-default-500 tw:border tw:border-t-0': showTopBar }"
      :style="`height: ${maxHeight ?? iframeHeight};${maxHeight ? ' overflow: hidden;' : ''}`"
    >
      <PreviewIframe :html="parsedHtml.split(edroneFooterStart)?.[0]" @load="setIframeHeight" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, onUnmounted, computed, watch } from "vue";
import { getExampleProducts } from "vue-app/api/ProductsService.ts";
import { BasicProductData } from "vue-app/utils/functions";
import PreviewIframe from "vue-app/components/PreviewIframe/PreviewIframe.vue";
import { useBrandingCache } from "vue-app/composables/useBrandingCache";
import EdroneSkeletonLoader from "edrone-components/EdroneSkeletonLoader.vue";

interface Props {
  showTopBar?: boolean;
  html?: string | null;
  seed?: number;
  forceLoading?: boolean;
  engagementTypeId?: number | null;
  maxHeight?: string;
}
const props = withDefaults(defineProps<Props>(), {
  showTopBar: true,
  html: "",
  seed: undefined,
  forceLoading: false,
  engagementTypeId: undefined,
  maxHeight: undefined,
});

const { getFooterHtml, getProductBanner, getAiBanner } = useBrandingCache();

const edroneFooterStart = '<div id="unlayer-edrone-frame"';
// we remove the footer from the html, because it is not needed in the preview. Div starts with this id and have some styles on it so we split on what it starts with

const iframeHeight = ref("100%");

const products = ref<BasicProductData[]>([]);
const dataLoading = ref(true);
const bannerUrl = ref("");
const aiBannerUrl = ref("");
const footerBranding = ref("");

const parsedHtml = computed(() => {
  if (props.forceLoading) {
    return "";
  }
  return parseHtml(props.html, products.value, bannerUrl.value, aiBannerUrl.value, footerBranding.value);
});

const loading = computed(() => props.forceLoading || dataLoading.value);

async function init() {
  const [productsData, banner, footer] = await Promise.all([
    getExampleProducts(props.seed),
    getProductBanner(),
    getFooterHtml(),
  ]);

  products.value = productsData;
  bannerUrl.value = banner;
  footerBranding.value = footer;

  if (props.html?.includes("*|BRANDING_AI_BANNER_URL|*")) {
    await fetchAiBannerUrl();
  }

  dataLoading.value = false;
}

const fetchAiBannerUrl = async () => {
  if (!props.engagementTypeId) {
    aiBannerUrl.value = "";
    return;
  }

  aiBannerUrl.value = await getAiBanner(props.engagementTypeId);
};

watch(
  () => props.engagementTypeId,
  async () => {
    if (props.html?.includes("*|BRANDING_AI_BANNER_URL|*")) {
      await fetchAiBannerUrl();
    }
  }
);

function setIframeHeight(document: Document) {
  const calculateHeight = () => {
    const bodyHeight = document.body.scrollHeight;
    const htmlHeight = document.documentElement.scrollHeight;
    const maxHeight = Math.max(bodyHeight, htmlHeight);
    iframeHeight.value = Math.round(maxHeight) + 2 + "px";
  };

  calculateHeight();

  // unlayer content is in a table with id="u_body", so we need to observe its height for correct height calculation
  const u_body = document.querySelector("#u_body") as HTMLElement;
  if (u_body) {
    resizeObserver.observe(u_body);
  } else {
    // Observe body as fallback
    resizeObserver.observe(document.body);
  }

  // Recalculate after images load
  setTimeout(() => {
    calculateHeight();
  }, 100);
}

const resizeObserver = new ResizeObserver((entries) => {
  entries.forEach((entry) => {
    const element = entry.target as HTMLElement;
    const doc = element.ownerDocument;
    if (doc) {
      const bodyHeight = doc.body.scrollHeight;
      const htmlHeight = doc.documentElement.scrollHeight;
      const maxHeight = Math.max(bodyHeight, htmlHeight, entry.contentRect.height);
      iframeHeight.value = Math.round(maxHeight) + 2 + "px";
    }
  });
});

onMounted(async () => {
  await init();
});

onUnmounted(() => {
  resizeObserver.disconnect();
});

function getProducts(products: BasicProductData[]) {
  const finalProductList: BasicProductData[] = [];
  if (products) {
    for (let i = 0; i < products.length; i++) {
      const p = products[i];
      if (p && p.title && p.price && p.image) {
        finalProductList.push(p);
      }
    }
  }
  return finalProductList;
}

function parseHtml(
  html: string | null,
  products: BasicProductData[],
  bannerUrl: string,
  aiBannerUrl: string,
  footerBranding: string
) {
  if (!html) {
    return "";
  }
  let currencySign = "$";
  if (window.EDRONE_CONTEXT.user.currency === "EUR") {
    currencySign = "€";
  } else if (window.EDRONE_CONTEXT.user.currency === "GBP") {
    currencySign = "£";
  } else if (window.EDRONE_CONTEXT.user.currency === "PLN") {
    currencySign = "zł";
  } else if (window.EDRONE_CONTEXT.user.currency === "BRL") {
    currencySign = "R$";
  }

  const finalProductList = getProducts(products);

  let parsedHtml = html;
  for (let i = 1; i <= 6; i++) {
    ["DYNAMIC_ITEM_", "BESTSELLER_ITEM_", "NEWEST_ITEM_", "RECOMMENDATED_ITEM_"].forEach((name) => {
      if (!parsedHtml.includes("*|" + name + i)) {
        return;
      }

      let price: string = "79,99";
      const product = finalProductList[i - 1];
      if (product && product.price) {
        price = new Intl.NumberFormat().format(product.price);
      }
      let oldPrice: string = "89,99";
      if (product && product.oldPrice) {
        oldPrice = new Intl.NumberFormat().format(product.oldPrice);
      }
      let title: string = "Example title";
      if (product && product.title) {
        title = product.title;
      }
      let image: string = "https://control.edrone.me/assets/newsletter-builder/_row_thumbs/1x-square.jpg";
      if (product && product.image) {
        image = product.image;
      }

      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_IMAGE_URL|*", image);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_TITLE|*", title);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_PRICE|*", price);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_OLD_PRICE|*", oldPrice);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_CURRENCY|*", currencySign);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_OLD_CURRENCY|*", currencySign);
    });
  }
  while (parsedHtml.includes("*|IMAGE_WIDTH|*")) {
    parsedHtml = parsedHtml.replace("*|IMAGE_WIDTH|*", "500");
  }
  const fontSize = parsedHtml.match(/data-title-font-size="(\d+)"/);
  const height = fontSize ? Number(fontSize[1]) * 3 : 48;
  while (parsedHtml.includes("*|TITLE_HEIGHT|*")) {
    parsedHtml = parsedHtml.replace("*|TITLE_HEIGHT|*", `${height}`);
  }
  while (parsedHtml.includes("*|TRACKER_ID|*")) {
    parsedHtml = parsedHtml.replace("*|TRACKER_ID|*", String(window.EDRONE_CONTEXT.trackerId));
  }
  while (parsedHtml.includes("*|CUSTOMER_EMAIL|*")) {
    parsedHtml = parsedHtml.replace("*|CUSTOMER_EMAIL|*", String(window.EDRONE_CONTEXT.user.email));
  }
  while (parsedHtml.includes("*|FRAME_UNSUBSCRIBE_TEXT|*")) {
    parsedHtml = parsedHtml.replace("*|FRAME_UNSUBSCRIBE_TEXT|*", "Unsubscribe");
  }
  while (parsedHtml.includes("*|BRANDING_BANNER_URL|*")) {
    parsedHtml = parsedHtml.replace("*|BRANDING_BANNER_URL|*", bannerUrl);
  }
  while (parsedHtml.includes("*|BRANDING_AI_BANNER_URL|*")) {
    parsedHtml = parsedHtml.replace("*|BRANDING_AI_BANNER_URL|*", aiBannerUrl);
  }

  if (
    !window.EDRONE_CONTEXT?.tracker?.trackerBranding?.useHeader ||
    !window.EDRONE_CONTEXT?.tracker?.trackerBranding?.useFooter
  ) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(parsedHtml, "text/html");

    if (!window.EDRONE_CONTEXT?.tracker?.trackerBranding?.useHeader) {
      const header = doc.getElementById("brandingHeader");
      if (header) {
        header.remove();
      }
    }

    if (!window.EDRONE_CONTEXT?.tracker?.trackerBranding?.useFooter) {
      const footer = doc.getElementById("brandingFooter");
      if (footer) {
        footer.remove();
      }
    }

    parsedHtml = doc.documentElement.outerHTML;
  }

  if (window.EDRONE_CONTEXT?.tracker?.trackerBranding?.useHeader) {
    while (parsedHtml.includes("*|BRANDING_LOGO|*")) {
      parsedHtml = parsedHtml.replace(
        "*|BRANDING_LOGO|*",
        window.EDRONE_CONTEXT?.tracker?.trackerBranding?.logoUrl || ""
      );
    }
  }

  if (window.EDRONE_CONTEXT?.tracker?.trackerBranding?.useFooter) {
    while (parsedHtml.includes("*|BRANDING_FOOTER|*")) {
      parsedHtml = parsedHtml.replace("*|BRANDING_FOOTER|*", footerBranding);
    }
  }

  parsedHtml = parsedHtml.replace(".u-row .u-col img {", ".notUsedInPreview {");
  parsedHtml = parsedHtml.replace(
    "</style>",
    "@media only screen and (max-width: 500px) { .product-title { height: auto !important } } .product-title p {overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; /* number of lines to show */ line-clamp: 2; -webkit-box-orient: vertical;} .u-row-footer--edrone .u-col-50 { width: 50% !important; max-width: 50% !important; min-width: 50% !important;}</style>"
  );

  parsedHtml = parsedHtml.replace(/<img(?![^>]*\sloading=)/gi, '<img loading="lazy"');

  return parsedHtml;
}
</script>
